import { defer, forkJoin, from, Observable, of } from "rxjs";
import { every, map, mergeMap } from "rxjs/operators";

import { Injectable } from "@angular/core";

import {
  AreaInfo,
  Model3DData,
  PointSet,
  PointSet_IV7,
  PointSet_NX,
  ProductInfo,
  StereoReconstructionResult,
  Vertices2Df
} from "../../_objects/remeasurement";
import { MeasurementIntermediateInfoDbService } from "../../_services/database/measurement-intermediate-info-db.service";
import { FileSystemAccessService } from "../../_services/file-system-access.service";
import { IPC_CHANNEL } from "../electron.constants";
import { MeasurementIntermediateInfoSchema } from "../models/measurement-intermediate-info.schema";
import { ElectronService } from "../../electron.service";

/**
 * オフライン用 Measurement サービス。
 */
@Injectable({
  providedIn: "root"
})
export class OfflineMeasurementService {
  public constructor(
    private electronService: ElectronService,
    private fileSystemAccessService: FileSystemAccessService,
    private measurementIntermediateInfoDbService: MeasurementIntermediateInfoDbService
  ) {}

  /**
   * PointCloud を使用して Model3DData を取得する。
   *
   * @param pointCloudFilePath 画像から生成した PointCloud ファイルのパス。
   * @returns base64 化された画像情報。
   */
  public get3DModelData(pointCloudFilePath: string): Observable<Model3DData> {
    return defer(() => this.fileSystemAccessService.readFile(pointCloudFilePath)).pipe(map((text) => JSON.parse(text) as Model3DData));
  }

  /**
   * PointCloud を使用して base64 化された画像情報を取得する。
   *
   * @param imageType 画像タイプ。
   * @param pointCloudFilePath 画像から生成した PointCloud ファイルのパス。
   * @returns base64 化された画像情報。
   */
  public get3DModelImageDataFile(imageType: string, pointCloudFilePath: string): Observable<string> {
    return defer(() => this.fileSystemAccessService.getSaveBlobFolderPath()).pipe(
      mergeMap((blobPath) =>
        of(this.electronService.sendSyncCommand(IPC_CHANNEL.MEASUREMENT.GET3DMODELIMAGEDATAFILE, ...[imageType, `${blobPath}${pointCloudFilePath}`]))
      ),
      map((x) => x as string)
    );
  }

  /**
   * GetLibraryVersion を実行する。
   *
   * @param imageType 画像タイプ。
   * @returns バージョン情報。
   */
  public getLibraryVersion(imageType: string): Observable<string> {
    return defer(() => of(this.electronService.sendSyncCommand(IPC_CHANNEL.MEASUREMENT.GETLIBRARYVERSION, ...[imageType]))).pipe(
      map((x) => x as string)
    );
  }

  /**
   * GetPointSet を実行する。
   *
   * @param imageType 画像タイプ。
   * @param internalDataFilePath 画像から生成した InternalData ファイルのパス。
   * @param outputJsonFilePath JSON 出力結果ファイルパス。
   * @param vertices2Df Vertices2Df データ。
   * @returns Measurment DLL 実行結果。
   */
  public getPointSet(
    imageType: string,
    internalDataFilePath: string,
    pointCloudFilePath: string,
    outputJsonFilePath: string,
    vertices2Df: Vertices2Df
  ): Observable<any> {
    return defer(() => this.fileSystemAccessService.getSaveBlobFolderPath()).pipe(
      mergeMap((blobPath) =>
        of(
          this.electronService.sendSyncCommand(
            IPC_CHANNEL.MEASUREMENT.GETPOINTSET,
            ...[imageType, `${blobPath}${internalDataFilePath}`, `${blobPath}${pointCloudFilePath}`, `${blobPath}${outputJsonFilePath}`, vertices2Df]
          )
        )
      )
    );
  }

  /**
   * GetPolyline を実行する。
   *
   * @param imageType 画像タイプ。
   * @param internalDataFilePath 画像から生成した InternalData ファイルのパス。
   * @param outputJsonFilePath JSON 出力結果ファイルパス。
   * @param vertices2Dfs Vertices2Df データの配列。
   * @param lineType LineType。
   * @param lr LR。
   * @returns Measurment DLL 実行結果。
   */
  public getPolyline(
    imageType: string,
    internalDataFilePath: string,
    outputJsonFilePath: string,
    vertices2Dfs: Array<Vertices2Df>,
    lineType: number,
    lr: number
  ): Observable<any> {
    return defer(() => this.fileSystemAccessService.getSaveBlobFolderPath()).pipe(
      mergeMap((blobPath) =>
        of(
          this.electronService.sendSyncCommand(
            IPC_CHANNEL.MEASUREMENT.GETPOLYLINE,
            ...[imageType, `${blobPath}${internalDataFilePath}`, `${blobPath}${outputJsonFilePath}`, vertices2Dfs, lineType, lr]
          )
        )
      )
    );
  }

  public measureMissingCorner(
    imageType: string,
    internalDataFilePath: string,
    outputJsonFilePath: string,
    pointCloudFilePath: string,
    pointSets: Array<PointSet>
  ): Observable<any> {
    return defer(() => this.fileSystemAccessService.getSaveBlobFolderPath()).pipe(
      mergeMap((blobPath) =>
        of(
          this.electronService.sendSyncCommand(
            IPC_CHANNEL.MEASUREMENT.MEASUREMISSINGCORNER,
            ...[imageType, `${blobPath}${internalDataFilePath}`, `${blobPath}${outputJsonFilePath}`, `${blobPath}${pointCloudFilePath}`, pointSets]
          )
        )
      )
    );
  }

  /**
   * MeasureArea を実行する。
   *
   * @param imageType 画像タイプ。
   * @param internalDataFilePath 画像から生成した InternalData ファイルのパス。
   * @param outputJsonFilePath JSON 出力結果ファイルパス。
   * @param pointSets PointSet データの配列。
   * @param pointCount PointCount。
   * @returns Measurment DLL 実行結果。
   */
  public measureArea(
    imageType: string,
    internalDataFilePath: string,
    outputJsonFilePath: string,
    pointSets: Array<PointSet_IV7> | Array<PointSet_NX>,
    pointCount: number
  ): Observable<any> {
    return defer(() => this.fileSystemAccessService.getSaveBlobFolderPath()).pipe(
      mergeMap((blobPath) =>
        of(
          this.electronService.sendSyncCommand(
            IPC_CHANNEL.MEASUREMENT.MEASUREAREA,
            ...[imageType, `${blobPath}${internalDataFilePath}`, `${blobPath}${outputJsonFilePath}`, pointSets, pointCount]
          )
        )
      )
    );
  }

  /**
   * MeasureDepth を実行する。
   *
   * @param imageType 画像タイプ。
   * @param internalDataFilePath 画像から生成した InternalData ファイルのパス。
   * @param outputJsonFilePath JSON 出力結果ファイルパス。
   * @param pointSets PointSet データの配列。
   * @param pointCount PointCount。
   * @returns Measurment DLL 実行結果。
   */
  public measureDepth(
    imageType: string,
    internalDataFilePath: string,
    outputJsonFilePath: string,
    pointCloudFilePath: string,
    pointSets: Array<PointSet_IV7> | Array<PointSet_NX>
  ): Observable<any> {
    return defer(() => this.fileSystemAccessService.getSaveBlobFolderPath()).pipe(
      mergeMap((blobPath) =>
        of(
          this.electronService.sendSyncCommand(
            IPC_CHANNEL.MEASUREMENT.MEASUREDEPTH,
            ...[imageType, `${blobPath}${internalDataFilePath}`, `${blobPath}${outputJsonFilePath}`, `${blobPath}${pointCloudFilePath}`, pointSets]
          )
        )
      )
    );
  }

  /**
   * MeasureDistance を実行する。
   *
   * @param imageType 画像タイプ。
   * @param internalDataFilePath 画像から生成した InternalData ファイルのパス。
   * @param outputJsonFilePath JSON 出力結果ファイルパス。
   * @param pointSets PointSet データの配列。
   * @returns Measurment DLL 実行結果。
   */
  public measureDistance(
    imageType: string,
    internalDataFilePath: string,
    outputJsonFilePath: string,
    pointSets: Array<PointSet_IV7> | Array<PointSet_NX>
  ): Observable<any> {
    return defer(() => this.fileSystemAccessService.getSaveBlobFolderPath()).pipe(
      mergeMap((blobPath) =>
        of(
          this.electronService.sendSyncCommand(
            IPC_CHANNEL.MEASUREMENT.MEASUREDISTANCE,
            ...[imageType, `${blobPath}${internalDataFilePath}`, `${blobPath}${outputJsonFilePath}`, pointSets]
          )
        )
      )
    );
  }

  /**
   * MeasureLines を実行する。
   *
   * @param imageType 画像タイプ。
   * @param internalDataFilePath 画像から生成した InternalData ファイルのパス。
   * @param outputJsonFilePath JSON 出力結果ファイルパス。
   * @param pointSets PointSet データの配列。
   * @param pointCount PointCount。
   * @returns Measurment DLL 実行結果。
   */
  public measureLines(
    imageType: string,
    internalDataFilePath: string,
    outputJsonFilePath: string,
    pointSets: Array<PointSet_IV7> | Array<PointSet_NX>,
    pointCount: number
  ): Observable<any> {
    return defer(() => this.fileSystemAccessService.getSaveBlobFolderPath()).pipe(
      mergeMap((blobPath) =>
        of(
          this.electronService.sendSyncCommand(
            IPC_CHANNEL.MEASUREMENT.MEASURELINES,
            ...[imageType, `${blobPath}${internalDataFilePath}`, `${blobPath}${outputJsonFilePath}`, pointSets, pointCount]
          )
        )
      )
    );
  }

  /**
   * MeasurePointToLine を実行する。
   *
   * @param imageType 画像タイプ。
   * @param internalDataFilePath 画像から生成した InternalData ファイルのパス。
   * @param outputJsonFilePath JSON 出力結果ファイルパス。
   * @param pointSets JSON 形式の PointSet データの配列。
   * @returns Measurment DLL 実行結果。
   */
  public measurePointToLine(
    imageType: string,
    internalDataFilePath: string,
    outputJsonFilePath: string,
    pointSets: Array<PointSet_IV7> | Array<PointSet_NX>
  ): Observable<any> {
    return defer(() => this.fileSystemAccessService.getSaveBlobFolderPath()).pipe(
      mergeMap((blobPath) =>
        of(
          this.electronService.sendSyncCommand(
            IPC_CHANNEL.MEASUREMENT.MEASUREPOINTTOLINE,
            ...[imageType, `${blobPath}${internalDataFilePath}`, `${blobPath}${outputJsonFilePath}`, pointSets]
          )
        )
      )
    );
  }

  /**
   * Reconstruct を実行する。
   *
   * @param inspectionImageId 検査画像 ID。
   * @param imageType ImageType。
   * @param imageFilePath 画像ファイルパス。
   * @param outputInternalDataFilePath InternalData ファイルのパス。
   * @param outputPointCloudFilePath 画像から生成した PointCloud ファイルのパス。
   * @param outputJsonFilePath JSON 出力結果ファイルパス。
   * @returns Measurment DLL 実行結果。
   */
  public reconstruct(
    inspectionImageId: string,
    imageType: string,
    imageFilePath: string,
    outputInternalDataFilePath: string,
    outputPointCloudFilePath: string,
    outputJsonFilePath: string
  ): Observable<any> {
    let stereoReconstructionResult: StereoReconstructionResult = null;
    return defer(() => this.fileSystemAccessService.getSaveBlobFolderPath()).pipe(
      mergeMap((blobPath) =>
        of(
          this.electronService.sendSyncCommand(
            IPC_CHANNEL.MEASUREMENT.RECONSTRUCT,
            ...[
              imageType,
              `${blobPath}${imageFilePath}`,
              `${blobPath}${outputInternalDataFilePath}`,
              `${blobPath}${outputPointCloudFilePath}`,
              `${blobPath}${outputJsonFilePath}`
            ]
          )
        )
      ),
      mergeMap((value) => {
        stereoReconstructionResult = value;
        stereoReconstructionResult.inspectionImageId = inspectionImageId;

        const measurementIntermediateInfo: MeasurementIntermediateInfoSchema = new MeasurementIntermediateInfoSchema();
        measurementIntermediateInfo.inspectionImageId = stereoReconstructionResult.inspectionImageId;
        measurementIntermediateInfo.productInfo = JSON.stringify(stereoReconstructionResult.productInfo);
        measurementIntermediateInfo.areaInfo = JSON.stringify(stereoReconstructionResult.areaInfo);
        measurementIntermediateInfo.type = stereoReconstructionResult.measurementType;
        measurementIntermediateInfo.libVersion = stereoReconstructionResult.libVersion;

        return this.measurementIntermediateInfoDbService.put(measurementIntermediateInfo);
      }),
      mergeMap(() => of(stereoReconstructionResult))
    );
  }

  /**
   * Reconstruct 情報を取得する。
   *
   * @param inspectionImageId 検査画像 ID。
   * @returns Reconstruct 情報。
   */
  public getReconstruction(inspectionImageId: string): Observable<StereoReconstructionResult> {
    return this.measurementIntermediateInfoDbService.get(inspectionImageId).pipe(
      mergeMap((value) => {
        if (!value) {
          return of(null);
        }

        const result: StereoReconstructionResult = new StereoReconstructionResult();
        result.inspectionImageId = value.inspectionImageId;
        result.libVersion = value.libVersion;
        result.measurementType = value.type;
        result.productInfo = JSON.parse(value.productInfo) as ProductInfo;
        result.areaInfo = JSON.parse(value.areaInfo) as AreaInfo;
        return of(result);
      })
    );
  }

  /**
   * InternalData が存在するかどうかを返す。
   *
   * @param internalDataFilePath InternalData ファイルパス。
   * @returns true … InternalData が存在する。
   */
  public isExistsInternalData(internalDataFilePath: string): Observable<boolean> {
    return defer(() => this.fileSystemAccessService.getSaveBlobFolderPath()).pipe(
      mergeMap((blobPath) =>
        of(this.electronService.sendSyncCommand(IPC_CHANNEL.MEASUREMENT.ISEXISTSINTERNALDATA, ...[`${blobPath}${internalDataFilePath}`]))
      ),
      map((x) => x as boolean)
    );
  }

  /**
   * 計測中間情報を削除する。
   *
   * @param inspectionImageId 検査画像 ID。
   * @param measurementDirectoryPath 検査情報ファイルのディレクトリーパス。
   * @returns true … 成功。
   */
  public deleteMeasurementIntermediateInfo(inspectionImageId: string, measurementDirectoryPath: string): Observable<boolean> {
    return forkJoin([
      this.measurementIntermediateInfoDbService.delete(inspectionImageId),
      defer(() => this.fileSystemAccessService.deleteFolder(measurementDirectoryPath))
    ]).pipe(map((values) => values.every((x) => x)));
  }

  /**
   * 計測中間情報を複数削除する。
   *
   * @param inspectionImageIds 検査画像 ID の配列。
   * @param measurementDirectoryPaths 検査情報ファイルのディレクトリーパスの配列。
   * @returns true … 成功。
   */
  public deleteMeasurementIntermediateInfos(inspectionImageIds: Array<string>, measurementDirectoryPaths: Array<string>): Observable<boolean> {
    return forkJoin([
      this.measurementIntermediateInfoDbService.deleteMultiple(inspectionImageIds),
      from(measurementDirectoryPaths).pipe(
        mergeMap((measurementDirectoryPath) => {
          return defer(() => this.fileSystemAccessService.deleteFolder(measurementDirectoryPath));
        }),
        every((x) => x)
      )
    ]).pipe(map((values) => values.every((x) => x)));
  }
}
